import { FunctionComponent, useCallback, useEffect, useMemo } from 'react';
import { Confirm, useRefresh } from 'react-admin';
import { useNavigate } from 'react-router-dom';

import useDeleteResource from '@app/hooks/useDeleteResource';
import { CASLAction, useAbility } from '@app/utils/authUtils';

import { ListHeader } from '@app/components/ListHeader';
import { HeaderAction, HeaderActionTypes, ListHeaderActions } from '@app/components/ListHeaderActions';
import { DefaultTable } from '@app/components/common/Table/DefaultTable';
import {
  ActionInterface,
  CUSTOM_ICON_TYPES,
  ColumnInterface,
  DeleteConfirmation,
  GenericObject,
} from '@app/components/common/Table/DefaultTable/types';

import './styles.css';

interface GenericListProps {
  resource: string;
  title: string;
  resourceSingular: string;
  rows?: GenericObject[];
  columns: ColumnInterface[];
  orderable?: boolean;
  generalFilterKey?: string;
  noDefaultHeaderActions?: boolean;
  hideSearch?: boolean;
  isListLoading: boolean;
  additionalHeaderActions?: HeaderAction[];
  replacementHeaderActions?: HeaderAction;
  additionalTableActions?: ActionInterface[];
  page?: number;
  SearchFields?: FunctionComponent;
  rowsPerPage?: number;
  generalFilterLabel?: string;
  total?: number;
  handleChangePage?(event: React.MouseEvent<HTMLButtonElement> | null, page: number): void;
  handleChangeRowsPerPage?(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>): void;
  reorderRecord?(dragIndex: number, hoverIndex: number): void;
  handleChangeLoading(loading: boolean): void;
  deleteCallback?(deletingId: number): void;
  handleChangeFilters?(filters: GenericObject): void;
  resolveDeleteConfirmationContent?(deletingId: number): Promise<DeleteConfirmation>;
  rowsPerPageOptions?: number[];
}

export const GenericList = ({
  resource,
  resourceSingular,
  total,
  generalFilterLabel,
  handleChangeLoading,
  title,
  columns,
  reorderRecord,
  generalFilterKey,
  hideSearch,
  orderable,
  isListLoading,
  noDefaultHeaderActions,
  additionalHeaderActions,
  additionalTableActions,
  SearchFields,
  page = 0,
  rowsPerPage = 10,
  handleChangePage,
  handleChangeRowsPerPage,
  deleteCallback,
  handleChangeFilters,
  rows,
  resolveDeleteConfirmationContent,
  rowsPerPageOptions,
}: GenericListProps) => {
  const refresh = useRefresh();
  const navigate = useNavigate();

  const {
    onDelete,
    deleteContent,
    isDeleteConfirmationOpen,
    isDeleteLoading,
    handleConfirmDelete,
    handleDeleteDialogClose,
  } = useDeleteResource(resource, resourceSingular, deleteCallback, resolveDeleteConfirmationContent);
  const ability = useAbility();

  useEffect(() => {
    handleChangeLoading(!!isListLoading);
  }, [isListLoading, handleChangeLoading]);

  const onEdit = (row: GenericObject) => {
    let isPromotion = window.location.pathname === '/prm-companies';
    navigate(`/${resource}/${row.id}${isPromotion ? '?promotion=true' : ''}`);
  };

  const onCreateNew = useCallback(() => {
    navigate(`/${resource}/create${ window.location.pathname === '/prm-companies' ? '?promotion=true' : ''}`);
  }, [navigate, resource]);

  const defaultTableActions = [];

  if (ability.can(CASLAction.WRITE, resource)) {
    defaultTableActions.push({ id: 'edit', icon: () => CUSTOM_ICON_TYPES.EDIT, action: onEdit, label: 'Edit' });
  }

  if (ability.can(CASLAction.DELETE, resource)) {
    defaultTableActions.push({ id: 'delete', icon: () => CUSTOM_ICON_TYPES.DELETE, action: onDelete, label: 'Delete' });
  }

  let tableActions = defaultTableActions;

  const canAddNew = ability.can(CASLAction.WRITE, resource);

  const defaultHeaderActions = useMemo(
    () => (canAddNew ? [{ id: 1, type: HeaderActionTypes.ADD_NEW, handler: onCreateNew }] : []),
    [onCreateNew, canAddNew]
  );

  const headerActions = useMemo(() => {
    if (noDefaultHeaderActions) {
      return additionalHeaderActions ?? [];
    }

    if (additionalHeaderActions) {
      return [...defaultHeaderActions, ...additionalHeaderActions];
    }

    return defaultHeaderActions;
  }, [additionalHeaderActions, defaultHeaderActions, noDefaultHeaderActions]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return (
    <div className="generic-list_container">
      <ListHeader
        hideSearch={hideSearch}
        generalFilterLabel={generalFilterLabel}
        generalFilterKey={generalFilterKey}
        title={title}
        SearchFields={SearchFields}
        handleChangeFilters={handleChangeFilters}
      />
      <ListHeaderActions headerActions={headerActions} />

      <DefaultTable
        resource={resource}
        title={title}
        reorderRecord={reorderRecord}
        orderable={orderable}
        rows={rows}
        columns={columns}
        total={total}
        page={page}
        rowsPerPage={rowsPerPage}
        handleChangePage={handleChangePage}
        handleChangeRowsPerPage={handleChangeRowsPerPage}
        rowsPerPageOptions={rowsPerPageOptions}
        actions={additionalTableActions ? [...additionalTableActions, ...tableActions] : tableActions}
      />
      <Confirm
        isOpen={isDeleteConfirmationOpen}
        loading={isDeleteLoading}
        title={`Delete ${resourceSingular}`}
        content={deleteContent as string}
        onConfirm={handleConfirmDelete}
        cancel="Cancel"
        confirm="Delete"
        onClose={handleDeleteDialogClose}
      />
    </div>
  );
};
